import {
  IconButton,
  styled,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import {AurDataGridColDef} from '../../../types/types';
import {GridRenderCellParams} from '@mui/x-data-grid';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {
  formatNumberWithCommaDash,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import {
  getDownloadIndexError,
} from '../../../api/EcaServices';

import {
  getDownloadDeleteIndexDocumentError,
  retryDeleteIndexDocument,
} from '../../../api/EcaManagementService';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {t} from 'i18next';
import {setShowToast} from '../../../redux/slices/indexingSlice';
import {setReRunPopup} from '../../../redux/slices/indexingManagementSlice';
import {
  buildRetryData,
  datasetName,
  indexingStatusKey,
  isCompletedWithErrorStatus,
  isDeleteDocumentsEvent,
} from '../model';
import {
  INDEXING_STANDARD_FULL_PROJECT,
  INDEXING_STANDARD_MEDIA,
} from '../../../constants/constants';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';

const IconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const renderCellHeaderIndex = (columnName: string) => {
  return (
    <div className="index-table-separator header-wrap ">
      <span className="typo-text  label4_size">{columnName}</span>
    </div>
  );
};

const tooltipTitle = () => {
  return (
    <Typography className="typo-text label3_size re-run-tooltip">
      {t(`indexes.reRunPopup.toolTipText1`)}
      <br />
      {t(`indexes.reRunPopup.toolTipText2`)}
    </Typography>
  );
};

const LightStyleTooltip = styled(({className, ...props}: TooltipProps) =>
  props?.title ? (
    <Tooltip
      {...props}
      classes={{popper: className}}
      placement="top"
      PopperProps={{
        className: 'aur-tooltip-index',
      }}
    />
  ) : null
)(({theme}: {theme: Theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 1)',
    fontSize: 11,
    marginTop: 5,
  },
}));
const LightTooltip = styled(({className, ...props}: TooltipProps) =>
  props?.title ? (
    <Tooltip
      {...props}
      classes={{popper: className, arrow: 'custom-arrow'}}
      placement="top"
      arrow
    />
  ) : null
)(({theme}: {theme: Theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    marginTop: -11,
  },
}));

const getDeleteDocumentsDataText = (row: any): string => {
  const deleteType = String(
    row.deleteType || row.deletionType || row.documentDeletionType || ''
  ).toLowerCase();
  const identifier = Array.isArray(row.identifiers)
    ? row.identifiers[0]
    : row.identifier;

  if (deleteType.includes('documentlist')) {
    return t('indexes.documentList');
  }

  if (row.mediaId) {
    return row.mediaId;
  }

  if (deleteType.includes('document') && identifier) {
    return identifier;
  }

  if (row.custodianName) {
    return row.custodianName;
  }

  if (row.custodian) {
    return row.custodian;
  }

  if (deleteType.includes('custodian') && identifier) {
    return identifier;
  }

  return (
    row.documentListName ||
    row.sourceName ||
    (row.name !== datasetName.deleteDocuments ? row.name : undefined) ||
    t('indexes.documentList')
  );
};

const getFailureReason = (row: any): string =>
  row.failureReason ||
  row.errorMessage ||
  row.statusMessage ||
  row.reason ||
  (isCompletedWithErrorStatus(row.status)
    ? t('indexes.completeWithErrors')
    : t('indexes.deleteDocumentsFailed'));

const renderStatusWithError = (
  statusLabel: string,
  failureReason: string,
  statusClassName = 'status-error'
) => (
  <div>
    <div className="status-error-container">
      <div className={`header-wrap ${statusClassName}`}>{statusLabel}</div>
      <LightTooltip
        title={
          <div className="typo-text label3_size index-status">
            {failureReason}
          </div>
        }
        aria-label={t('accessibility.indexManagement.moreInformation')}
        aria-describedby="indexStatusErrorHint"
      >
        <Typography>
          <AurIcon icon={IconNames.Error} color={IconColors.Critical} />
        </Typography>
      </LightTooltip>
      <span id="indexStatusErrorHint" style={{display: 'none'}}>
        {failureReason}
      </span>
    </div>
  </div>
);

const isNlpIndexRow = (row: any): boolean =>
  Number(row?.ecaNlpingestionId) > 0;

const isDeleteBuildType = (row: any): boolean =>
  [row?.buildType, row?.datasetName].some((value) => {
    const normalizedValue = value?.replace(/\s/g, '').toLowerCase();
    return (
      normalizedValue === 'delete' || normalizedValue === 'deletedocuments'
    );
  });

const isMaintenanceActionRow = (row: any): boolean => {
  return isDeleteDocumentsEvent(row) && !isNlpIndexRow(row);
};

const downloadFailedDocs = async (
  row: any,
  clientId: string | null | undefined,
  projectId: string | null | undefined,
  dispatch: (action: any) => void
) => {
  try {
    const queryParams = `?clientId=${clientId}&projectId=${projectId}`;
    const response = isMaintenanceActionRow(row)
      ? await getDownloadDeleteIndexDocumentError(queryParams, row?.indexId)
      : await getDownloadIndexError(
          queryParams,
          row?.indexId,
          isNlpIndexRow(row) && isDeleteBuildType(row)
            ? row?.ecaNlpingestionId
            : undefined
        );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'indexErrorTemplate.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading the template:', error);
    dispatch(setShowToast(true));
  }
};

const retryMaintenanceAction = async (
  row: any,
  clientId: string | null | undefined,
  projectId: string | null | undefined,
  dispatch: (action: any) => void
) => {
  try {
    const queryParams = `?clientId=${clientId}&projectId=${projectId}`;
    await retryDeleteIndexDocument(queryParams, row?.indexId);
  } catch (error) {
    console.error('Error retrying delete-document event:', error);
    dispatch(setShowToast(true));
  }
};

export const getIndexColumns = (): AurDataGridColDef[] => [
  {
    field: 'expand',
    width: 20,
    sortable: false,
    renderHeader: () => (
      <div className="sort-img-header-div aur-datagrid-column-separator">
        <span className="sr-only">
          {t('buttons.expand')} / {t('buttons.collapse')}{' '}
        </span>
      </div>
    ),
    renderCell: (params: GridRenderCellParams) => {
      const isExpanded = params.row.isExpanded;
      const parentType = params.row.type;

      // Check the parentType and render accordingly
      if (parentType === 'info') {
        return <span hidden />;
      }

      if (parentType === 'parent') {
        return (
          <>
            <IconButton
              onClick={() =>
                params.row.toggleExpansion?.(
                  params.row.id,
                  isExpanded,
                  params.row.indexId,
                  params.row.ecaNlpingestionId
                )
              }
              className="expand-icon"
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? t('accessibility.indexManagement.detail.closeAccordion')
                  : t('accessibility.indexManagement.detail.dataSetAccordion')
              }
              aria-description={
                isExpanded
                  ? t('accessibility.indexManagement.detail.closeAccordionHint')
                  : t(
                      'accessibility.indexManagement.detail.dataSetAccordionHint'
                    )
              }
            >
              <IconWrapper aria-hidden={true}>
                {isExpanded ? (
                  <AurIcon
                    icon={IconNames.CaretUp}
                    color={IconColors.Custom}
                    customClassName="aur-responsive-tabset-dropdown-icon-color"
                  />
                ) : (
                  <AurIcon
                    icon={IconNames.CaretDown}
                    color={IconColors.Custom}
                    customClassName="aur-responsive-tabset-dropdown-icon-color"
                  />
                )}
              </IconWrapper>
            </IconButton>
            <span>{params.row.buildType}</span>
          </>
        );
      }

      return null; // Return null if no matching condition
    },
  },

  {
    field: 'datasetName',
    headerName: t('ecaHomepageModals.indexTable.buildType'),
    width: 80,
    sortable: false,
    flex: 1,
  },

  {
    field: 'status',
    headerName: t('ecaHomepageModals.indexTable.status'),
    width: 120,
    sortable: false,

    renderCell: (params) => {
      const status = params?.row?.status;
      const failedDocCount: number = params?.row.failedDocCount;
      const partiallyFailedDocCount: number = params?.row.partialFailedDocCount;

      if (
        status === indexingStatusKey.inProgress ||
        status === indexingStatusKey.InProgress
      ) {
        return (
          <div className="cell-container" tabIndex={params.tabIndex}>
            <Typography className="typo-text body3_size list-text inprogress-color">
              {t('indexingStatus.inProgress')}{' '}
            </Typography>
            <Typography className="typo-text body4_size list-text mist-700">
              {failedDocCount}/{partiallyFailedDocCount}
            </Typography>
          </div>
        );
      }

      if (status === indexingStatusKey.complete) {
        return <span>{t('indexingStatus.completed')}</span>;
      }

      if (isCompletedWithErrorStatus(status)) {
        return renderStatusWithError(
          t('indexingStatus.completedWithError'),
          getFailureReason(params.row)
        );
      }

      if (
        status === indexingStatusKey.queued ||
        status === indexingStatusKey.pending
      ) {
        return <span className="completed-status">{status}</span>;
      }

      if (
        status === indexingStatusKey.failed &&
        isDeleteDocumentsEvent(params.row)
      ) {
        return renderStatusWithError(
          status,
          getFailureReason(params.row),
          'error-color delete-documents-status-error'
        );
      }

      return <span className="error-color">{status}</span>;
    },
  },
  {
    field: 'datasetName1',
    headerName: t('ecaHomepageModals.indexTable.data'),
    width: 80,
    sortable: false,
    renderCell: (params) => {
      let tooltipText = '';
      let displayText = '';

      if (params.row.datasetName === datasetName.overlayMediaAPCUpdate) {
        tooltipText = `${INDEXING_STANDARD_MEDIA} ${params.row.mediaId ?? ''}`;
        displayText = `${INDEXING_STANDARD_MEDIA} ${params.row.mediaId ?? ''}`;
      } else if (
        params.row.datasetName === datasetName.overlayProjectAPCUpdate
      ) {
        tooltipText = `${INDEXING_STANDARD_FULL_PROJECT} `;
        displayText = `${INDEXING_STANDARD_FULL_PROJECT} `;
      } else if (isDeleteDocumentsEvent(params.row)) {
        tooltipText = getDeleteDocumentsDataText(params.row);
        displayText = tooltipText;
      } else {
        tooltipText = `${params.row.name ?? ''}`;
        displayText = `${params.row.name ?? ''}`;
      }

      return (
        <LightTooltip title={<div>{tooltipText}</div>}>
          <Typography className="typo-text label3_size custom-color-eca sub-index-type data-set-name">
            {displayText}
          </Typography>
        </LightTooltip>
      );
    },
  },
  {
    field: 'startDateTime',

    renderHeader: () => (
      <LightStyleTooltip
        data-testid="index-tooltip"
        title={
          <div>
            <span className="typo-text label3_size">
              {t('indexes.dateFormat')}
            </span>
            <br />
            <span className="typo-text label3_size">
              {t('indexes.dateTimeStamp')}
            </span>
          </div>
        }
      >
        <div className="aur-datagrid-column-separator">
          {t('indexes.start')}
        </div>
      </LightStyleTooltip>
    ),
    width: 106,
    sortable: false,
    renderCell: (params) => {
      const startTime = params?.row?.startDateTime;
      return (
        params.row.type !== 'info' && (
          <LightTooltip
            title={
              <div>{startTime ? t('indexes.dateTimeStamp') : '- - -'}</div>
            }
          >
            <Typography className="typo-text body3_size list-text header-wrap">
              {startTime ? getUTCDateTimeForDisplay(startTime) : '- - -'}{' '}
            </Typography>
          </LightTooltip>
        )
      );
    },
  },
  {
    field: 'endDateTime',
    headerName: t('ecaHomepageModals.indexTable.end'),
    renderHeader: () => (
      <LightStyleTooltip
        data-testid="index-tooltip"
        title={
          <div>
            <span className="typo-text label3_size">
              {t('indexes.dateFormat')}
            </span>
            <br />
            <span className="typo-text label3_size">
              {t('indexes.dateTimeStamp')}
            </span>
          </div>
        }
      >
        <div className="aur-datagrid-column-separator">{t('indexes.end')}</div>
      </LightStyleTooltip>
    ),
    width: 106,
    sortable: false,
    renderCell: (params) => {
      const endTime = params?.row?.endDateTime;
      return (
        params.row.type !== 'info' && (
          <LightTooltip
            title={<div>{endTime ? t('indexes.dateTimeStamp') : '- - -'}</div>}
          >
            <Typography
              className={`${endTime ? 'typo-text body3_size list-text header-wrap' : 'typo-text body3_size three-dash-typo'}`}
            >
              {endTime ? getUTCDateTimeForDisplay(endTime) : '- - -'}{' '}
            </Typography>
          </LightTooltip>
        )
      );
    },
  },
  {
    field: 'initiatedByUser',
    headerName: t('ecaHomepageModals.indexTable.initiatedBy'),
    width: 100,
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      const name = params?.row?.initiatedByUser;
      return (
        <LightTooltip title={<div>{name}</div>}>
          <Typography className="typo-text body3_size list-text header-wrap">
            {name}{' '}
          </Typography>
        </LightTooltip>
      );
    },
  },
  // commented as per ticket arhd-11288
  // {
  //   field: 'startDocCount',
  //   headerName: 'Doc Count Before Indexing Event',
  //   width: 104,
  //   sortable: false,
  //   renderHeader: () =>
  //     renderCellHeaderIndex('Doc Count Before Indexing Event'),
  //   renderCell: (params) => {
  //     const startDocCount = params?.row?.startDocCount;
  //     return (
  //       params.row.type !== 'info' && (
  //         <Typography
  //           className={`${params?.row?.endDateTime ? 'typo-text body3_size list-text header-wrap' : 'typo-text body3_size three-dash-typo'}`}
  //         >
  //           {startDocCount ? formatNumberWithComma(startDocCount) : '- - -'}{' '}
  //         </Typography>
  //       )
  //     );
  //   },
  // },
  // commented as per ticket ARHD-17624
  // {
  //   field: 'endDocCount',
  //   headerName: t('ecaHomepageModals.indexTable.docCountAtEnd'),
  //   width: 104,
  //   sortable: false,
  //   renderHeader: () =>
  //     renderCellHeaderIndex(t('ecaHomepageModals.indexTable.docCountAtEnd')),

  //   renderCell: (params) => {
  //     const endDocCount = params?.row?.endDocCount;
  //     return (
  //       params.row.type !== 'info' && (
  //         <Typography className="typo-text body3_size list-text header-wrap">
  //           {endDocCount && formatNumberWithCommaDash(endDocCount)}
  //         </Typography>
  //       )
  //     );
  //   },
  // },
  {
    field: 'actedOnDocCount',
    headerName: t('ecaHomepageModals.indexTable.docCountActedUpon'),
    width: 96,
    sortable: false,
    renderHeader: () =>
      renderCellHeaderIndex(
        t('ecaHomepageModals.indexTable.docCountActedUpon')
      ),

    renderCell: (params) => {
      const actedOnDocCount = params?.row?.actedOnDocCount;
      return (
        params.row.type !== 'info' && (
          <Typography className="typo-text body3_size list-text header-wrap">
            {actedOnDocCount !== undefined &&
              actedOnDocCount !== null &&
              formatNumberWithCommaDash(actedOnDocCount)}
          </Typography>
        )
      );
    },
  },
  {
    field: 'indexedDocCount',
    headerName: t('ecaHomepageModals.indexTable.docsSuccessfullyIndexed'),
    width: 112,
    sortable: false,
    flex: 1,
    renderHeader: () =>
      renderCellHeaderIndex(
        t('ecaHomepageModals.indexTable.docsSuccessfullyIndexed')
      ),

    renderCell: (params) => {
      const totalDocCount = isDeleteDocumentsEvent(params.row)
        ? (params?.row?.indexedDocCount ?? params?.row?.totalDocCount)
        : params?.row?.totalDocCount;
      return (
        params.row.type !== 'info' && (
          <Typography className="typo-text body3_size list-text header-wrap">
            {totalDocCount !== undefined &&
              totalDocCount !== null &&
              formatNumberWithCommaDash(totalDocCount)}
          </Typography>
        )
      );
    },
  },
  {
    field: 'failed/partialDocCount',
    headerClassName: 'partialDocs',
    headerName: t('ecaHomepageModals.indexTable.docsUnsuccessfullyIndexed'),
    width: 145,
    sortable: false,
    renderHeader: () =>
      renderCellHeaderIndex(
        t('ecaHomepageModals.indexTable.docsUnsuccessfullyIndexed')
      ),
    renderCell: (params) => {
      const failedDocCount: number = params?.row.failedDocCount ?? 0;
      const partiallyFailedDocCount: number =
        params?.row.partialFailedDocCount ?? 0;
      const totalCount = failedDocCount + partiallyFailedDocCount;
      const showIcon = params?.row?.showDownloadIcon;
      const status = params?.row?.status;
      const shouldHideDocsColumnDownloadIcon =
        isMaintenanceActionRow(params.row) && isCompletedWithErrorStatus(status);
      const shouldHideNlpFailedDownloadIcon =
        isNlpIndexRow(params.row) && status === indexingStatusKey.failed;
      const dispatch = useDispatch();
      const {clientId, projectId} = useSelector(
        (state: RootState) => state.reports
      );
      let isFetching = false;
      const fetchData = async () => {
        isFetching = true;
        try {
          await downloadFailedDocs(params.row, clientId, projectId, dispatch);
        } catch (error) {
          dispatch(setShowToast(true));
        } finally {
          isFetching = false;
        }
      };
      return (
        params.row.type != 'info' && (
          <div className="failed-container">
            <div className="cell-container" tabIndex={params.tabIndex}>
              <Typography className="typo-text body3_size list-text">
                {formatNumberWithCommaDash(totalCount)}{' '}
              </Typography>
              {status === 'Complete with Errors' && (
                <Typography className="typo-text body4_size list-text mist-700">
                  ({formatNumberWithCommaDash(failedDocCount)}/
                  {formatNumberWithCommaDash(partiallyFailedDocCount)})
                </Typography>
              )}
            </div>
            {showIcon &&
              !shouldHideDocsColumnDownloadIcon &&
              !shouldHideNlpFailedDownloadIcon && (
              <div className="index-failed-download-icon">
                <IconButton
                  disabled={isFetching}
                  onClick={() => {
                    fetchData();
                  }}
                  className="expand-icon "
                  style={{cursor: isFetching ? 'not-allowed' : 'pointer'}}
                  aria-label={t('indexes.downloadFailedDocsIcon')}
                  aria-description={t('eca.overlay.downloadErrorReportMsg')}
                >
                  <LightTooltip
                    title={
                      <div className="typo-text label3_size">
                        {t('indexes.downloadFailedDocsIcon')}
                      </div>
                    }
                    aria-hidden={true}
                  >
                    <div
                      className="index-download-icon"
                      style={{cursor: isFetching ? 'not-allowed' : 'pointer'}}
                    >
                      {isFetching ? (
                        <AurLoadingIndicator size={16} />
                      ) : (
                        <AurSvgIcon
                          src="images/download_icon.svg"
                          ariaHidden={true}
                        />
                      )}
                    </div>
                  </LightTooltip>
                </IconButton>
              </div>
            )}
          </div>
        )
      );
    },
  },
  {
    field: 'a',
    headerName: t('ecaHomepageModals.indexTable.action'),
    width: 72,
    isBorderNotRequired: true,
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      const dispatch = useDispatch();
      const status = params?.row?.status;
      const dataset = params?.row?.datasetName;
      const isCompletedWithErrors = isCompletedWithErrorStatus(status);
      const isFailed = status === indexingStatusKey.failed;
      const shouldShowAdditionalLinks =
        isMaintenanceActionRow(params.row) && (isCompletedWithErrors || isFailed);
      const shouldShowNlpDeleteRetryButton =
        isNlpIndexRow(params.row) &&
        isDeleteBuildType(params.row) &&
        (isFailed || isCompletedWithErrors);
      const shouldShowOriginalRetryButton =
        (isFailed || isCompletedWithErrors) &&
        (dataset === datasetName.overlay ||
          dataset === datasetName.newExport ||
          dataset === datasetName.overlayMediaAPCUpdate ||
          dataset === datasetName.overlayProjectAPCUpdate);
      const {clientId, projectId} = useSelector(
        (state: RootState) => state.reports
      );

      if (
        shouldShowOriginalRetryButton ||
        shouldShowAdditionalLinks ||
        shouldShowNlpDeleteRetryButton
      ) {
        return (
          <div className="index-failed-download-icon action-links">
            {shouldShowAdditionalLinks && isCompletedWithErrors && (
              <IconButton
                onClick={() => {
                  downloadFailedDocs(
                    params.row,
                    clientId,
                    projectId,
                    dispatch
                  );
                }}
                className="expand-icon "
                aria-label={t('indexes.downloadFailedDocsIcon')}
                aria-description={t('eca.overlay.downloadErrorReportMsg')}
              >
                <LightTooltip
                  title={
                    <div className="typo-text label3_size">
                      {t('indexes.downloadFailedDocsIcon')}
                    </div>
                  }
                  aria-hidden={true}
                >
                  <div className="index-download-icon">
                    <AurSvgIcon src="images/download_icon.svg" ariaHidden={true} />
                  </div>
                </LightTooltip>
              </IconButton>
            )}
            {(shouldShowOriginalRetryButton || shouldShowNlpDeleteRetryButton) && (
              <IconButton
                onClick={() => {
                  dispatch(
                    setReRunPopup({
                      showpopup: true,
                      dataSetName: params.row.datasetName,
                      indexId: params?.row?.indexId,
                      ecaNlpingestionId: params?.row?.ecaNlpingestionId,
                      exportId: params?.row?.ecaExportId,
                      datFilePath: params?.row?.datFilePath,
                      mediaId: params?.row?.mediaId,
                      overlayRetryData: buildRetryData(params.row.payload),
                    })
                  );
                }}
                className="expand-icon"
                aria-label={`${t('indexes.reRunPopup.reRunButtonHint')}`}
              >
                <LightTooltip title={tooltipTitle()} aria-hidden={true}>
                  <div className="index-button-icon">
                    <AurSvgIcon src="images/eca_rerun.svg" ariaHidden={true} />
                  </div>
                </LightTooltip>
              </IconButton>
            )}
            {shouldShowAdditionalLinks && (
              <IconButton
                onClick={() => {
                  retryMaintenanceAction(
                    params.row,
                    clientId,
                    projectId,
                    dispatch
                  );
                }}
                className="expand-icon"
                aria-label={`${t('indexes.reRunPopup.reRunButtonHint')}`}
              >
                <LightTooltip title={tooltipTitle()} aria-hidden={true}>
                  <div className="index-button-icon">
                    <AurSvgIcon src="images/eca_rerun.svg" ariaHidden={true} />
                  </div>
                </LightTooltip>
              </IconButton>
            )}
          </div>
        );
      }
    },
  },
];
